/**
 * /status — Layer capability & health board.
 *
 * Everything Layer can do, grouped into ~50 named business capabilities across
 * 14 categories, each showing a live health verdict (ok / stale / failed / idle
 * / off) computed from existing audit data + the durable daily self-test, with
 * drill-down to the underlying canonical actions / sagas / crons / connectors
 * and a per-capability "Run test now". A final "All actions" catch-all lists
 * every remaining registered unit so nothing escapes the board.
 *
 * Sourced from `GET /api/status`; "Run test now" posts to
 * `/api/status/run-test`. Auto-refreshes every 30s. Staff-facing, read-mostly.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  ApiError,
  type StatusResponse,
  type StatusCapabilityState,
  type StatusHealth,
} from "../lib/api";
import type {
  CatalogActionEntry,
  CatalogResponse,
} from "@layer/dashboard/types";
import { DispatchForm } from "../components/DispatchForm";
import { ActionModalButton } from "../components/ActionModalButton";
import { LookupRefreshProvider } from "../lib/lookup-refresh";
import { studioTimeZone } from "../lib/studio-tz";

const REFRESH_MS = 30_000;

const HEALTH_LABEL: Record<StatusHealth, string> = {
  ok: "OK",
  stale: "Stale",
  failed: "Failed",
  never: "Idle",
  dark: "Off",
};

function fmtWhen(iso: string | null): string {
  if (iso === null) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const deltaSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (deltaSec < 0)
    return d.toLocaleString("en-GB", { timeZone: studioTimeZone() });
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  if (deltaSec < 86400 * 30) return `${Math.floor(deltaSec / 86400)}d ago`;
  return d.toLocaleDateString("en-GB", { timeZone: studioTimeZone() });
}

function HealthBadge({ health }: { health: StatusHealth }): JSX.Element {
  return (
    <span className={`status-badge status-${health}`}>
      <span className="status-dot" aria-hidden="true" />
      {HEALTH_LABEL[health]}
    </span>
  );
}

function CapabilityCard({
  cap,
  onRunTest,
  running,
  onRunWith,
}: {
  cap: StatusCapabilityState;
  onRunTest: (cap: StatusCapabilityState) => void;
  running: boolean;
  onRunWith?: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const testable = cap.safety !== "passive-only";
  const isSyntheticSend =
    cap.probe === "synthetic-whatsapp" || cap.probe === "synthetic-email";
  return (
    <div className={`status-card status-card-${cap.health}`}>
      <div className="status-card-head">
        <HealthBadge health={cap.health} />
        <strong className="status-card-name">{cap.name}</strong>
      </div>
      <p className="status-card-desc">{cap.description}</p>
      <dl className="status-card-meta">
        <div>
          <dt>Last run</dt>
          <dd>{fmtWhen(cap.last_run_at)}</dd>
        </div>
        {/* "Last self-test" only on cards that actually get self-tested
            (safe-auto = daily cron, safe-manual = on a button click). The
            passive-only cards never run a self-test, so the line was always
            "—" there — Mario asked to drop it everywhere it's not applicable. */}
        {testable ? (
          <div>
            <dt>Last self-test</dt>
            <dd>
              {cap.last_self_test_at === null ? (
                "—"
              ) : (
                <>
                  {fmtWhen(cap.last_self_test_at)}{" "}
                  <span className={cap.self_test_ok ? "status-ok-text" : "status-failed-text"}>
                    {cap.self_test_ok ? "✓" : `✗ ${cap.error_code ?? "failed"}`}
                  </span>
                </>
              )}
            </dd>
          </div>
        ) : null}
      </dl>
      {cap.error_code !== null && cap.health === "failed" ? (
        <p className="status-card-error">⚠ {cap.error_code}</p>
      ) : null}
      <div className="status-card-actions">
        <button
          type="button"
          className="status-drill-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "▾" : "▸"} {cap.units.length} unit
          {cap.units.length === 1 ? "" : "s"}
        </button>
        {testable ? (
          <button
            type="button"
            className="status-runtest"
            disabled={running}
            onClick={() => onRunTest(cap)}
          >
            {running
              ? "Testing…"
              : isSyntheticSend
                ? "Run test now (sends to admin)"
                : "Run test now"}
          </button>
        ) : null}
        {onRunWith ? (
          <button
            type="button"
            className="status-runwith"
            onClick={onRunWith}
          >
            Run with data…
          </button>
        ) : null}
      </div>
      {open ? (
        <table className="status-units">
          <tbody>
            {cap.unit_details.map((u) => (
              <tr key={u.unit}>
                <td>
                  <code>{u.unit}</code>{" "}
                  <span className="status-unit-kind">{u.kind}</span>
                </td>
                <td>{fmtWhen(u.last_at)}</td>
                <td>
                  {u.ok === null
                    ? "—"
                    : u.ok
                      ? "✓"
                      : `✗${u.status ? ` ${u.status}` : ""}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

interface RunTarget {
  readonly title: string;
  readonly units: readonly string[];
}

/**
 * Per-card "Run with data…" modal — reuses the shared <DispatchForm>
 * (schema-compiled form → POST /api/dispatch/:action as human:staff). Lets an
 * operator run any of the capability's staff-dispatchable actions against the
 * input they type (so the passive-only cards become testable on test data the
 * operator chooses). No safety gate — the operator owns what they enter.
 */
function StatusRunModal({
  target,
  actionMap,
  onClose,
}: {
  target: RunTarget;
  actionMap: ReadonlyMap<string, CatalogActionEntry>;
  onClose: () => void;
}): JSX.Element {
  const dispatchable = target.units.filter((u) => actionMap.has(u));
  const [selected, setSelected] = useState<string>(dispatchable[0] ?? "");
  const action = actionMap.get(selected);
  return (
    <div
      className="row-edit-modal-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="row-edit-modal status-run-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="status-run-modal-head">
          <h3 className="row-edit-modal-title">Run · {target.title}</h3>
          <button
            type="button"
            className="row-edit-modal-cancel"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <p className="row-edit-modal-note">
          Dispatches through the chokepoint as <code>human:staff</code> with the
          input you enter — it runs for real against whatever you provide, so use
          test data.
        </p>
        {dispatchable.length === 0 || action === undefined ? (
          <p className="empty">
            No staff-dispatchable action here — this capability is driven by a
            cron/connector. Use “Run test now”.
          </p>
        ) : (
          <LookupRefreshProvider>
            {dispatchable.length > 1 ? (
              <label className="status-run-action-pick">
                <span>Action</span>
                <select
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                >
                  {dispatchable.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <p className="status-run-single-action">
                <code>{selected}</code>
              </p>
            )}
            <DispatchForm key={action.name} action={action} />
          </LookupRefreshProvider>
        )}
      </div>
    </div>
  );
}

export function Status(): JSX.Element {
  const [data, setData] = useState<StatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [running, setRunning] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [showCatchAll, setShowCatchAll] = useState(false);
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [runWith, setRunWith] = useState<RunTarget | null>(null);
  const cancelledRef = useRef(false);

  // Catalog backs the "Run with data…" forms (action input schemas). Staff-
  // dispatchable actions only — keyed by name for unit → action resolution.
  useEffect(() => {
    void api
      .catalog()
      .then((c) => {
        if (!cancelledRef.current) setCatalog(c);
      })
      .catch(() => {});
  }, []);
  const staffActionMap = useMemo<ReadonlyMap<string, CatalogActionEntry>>(() => {
    const m = new Map<string, CatalogActionEntry>();
    for (const a of catalog?.actions ?? []) {
      if (a.allowed_actors.includes("human:staff")) m.set(a.name, a);
    }
    return m;
  }, [catalog]);

  const load = useCallback(async (): Promise<void> => {
    try {
      const r = await api.status();
      if (cancelledRef.current) return;
      setData(r);
      setError(null);
      setUpdatedAt(new Date());
    } catch (e) {
      if (cancelledRef.current) return;
      setError(
        e instanceof ApiError ? e.code ?? `http_${e.status}` : (e as Error).message,
      );
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    void load();
    const id = setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelledRef.current = true;
      clearInterval(id);
    };
  }, [load]);

  // Swap one capability's state in place after a "Run test now".
  const replaceCapability = useCallback((next: StatusCapabilityState): void => {
    setData((prev) =>
      prev === null
        ? prev
        : {
            ...prev,
            categories: prev.categories.map((g) => ({
              ...g,
              capabilities: g.capabilities.map((c) =>
                c.id === next.id ? next : c,
              ),
            })),
          },
    );
  }, []);

  const runTest = useCallback(
    async (cap: StatusCapabilityState): Promise<void> => {
      const isSyntheticSend =
        cap.probe === "synthetic-whatsapp" || cap.probe === "synthetic-email";
      let confirmSend = false;
      if (isSyntheticSend) {
        // eslint-disable-next-line no-alert
        const ok = window.confirm(
          `"${cap.name}" sends a REAL message to the studio admin/manager number. Send it now?`,
        );
        if (!ok) return;
        confirmSend = true;
      }
      setRunning(cap.id);
      setTestError(null);
      try {
        const updated = await api.runStatusTest(cap.id, confirmSend);
        replaceCapability(updated);
      } catch (e) {
        setTestError(
          e instanceof ApiError ? e.code ?? `http_${e.status}` : (e as Error).message,
        );
      } finally {
        setRunning(null);
      }
    },
    [replaceCapability],
  );

  if (loading && data === null) return <p className="empty">Loading status…</p>;
  if (error !== null && data === null) {
    return <p className="empty">Couldn&apos;t load status ({error}).</p>;
  }
  if (data === null) return <p className="empty">No data.</p>;

  const s = data.summary;

  return (
    <div>
      <h1 className="page-title">Status</h1>
      <p className="member-note">
        Everything Layer can do, and whether it&apos;s working — checked live +
        self-tested daily.
        {updatedAt ? (
          <>
            {" · "}
            <span className="subtle">
              updated{" "}
              {updatedAt.toLocaleTimeString("en-GB", {
                timeZone: studioTimeZone(),
              })}{" "}
              studio time (auto every 30s)
            </span>
          </>
        ) : null}
      </p>

      <div className="status-summary">
        <span className="status-badge status-ok">
          <span className="status-dot" /> {s.ok} OK
        </span>
        <span className="status-badge status-stale">
          <span className="status-dot" /> {s.stale} stale
        </span>
        <span className="status-badge status-failed">
          <span className="status-dot" /> {s.failed} failed
        </span>
        <span className="status-badge status-never">
          <span className="status-dot" /> {s.never} idle
        </span>
        <span className="status-badge status-dark">
          <span className="status-dot" /> {s.dark} off
        </span>
        <span className="subtle">· {s.total} capabilities</span>
      </div>

      {testError !== null ? (
        <p className="status-card-error">Run-test failed: {testError}</p>
      ) : null}

      <section className="panel">
        <h2>Operator tools</h2>
        <p className="member-note">
          On-demand operator actions that previously lived only in the action
          catalog.
        </p>
        <div className="card-head-actions">
          <ActionModalButton
            action="vendor_health_check"
            buttonLabel="Test a connector"
            modalTitle="Vendor health check"
            description="Ping a vendor's API and report ok + latency."
            submitLabel="Run check"
            fields={[
              {
                kind: "select",
                name: "vendor",
                label: "Vendor",
                required: true,
                options: [
                  { value: "stripe", label: "Stripe" },
                  { value: "resend", label: "Resend" },
                  { value: "whatsapp", label: "WhatsApp" },
                  { value: "klaviyo", label: "Klaviyo" },
                ],
              },
            ]}
            renderSuccess={(resp) => (
              <pre className="action-result-json">
                {JSON.stringify(resp, null, 2)}
              </pre>
            )}
          />
        </div>
      </section>

      {data.categories.map((group) => (
        <section className="panel" key={group.category}>
          <h2>{group.category}</h2>
          <div className="status-grid">
            {group.capabilities.map((cap) => {
              const canRunWith = cap.units.some((u) => staffActionMap.has(u));
              return (
                <CapabilityCard
                  key={cap.id}
                  cap={cap}
                  onRunTest={runTest}
                  running={running === cap.id}
                  {...(canRunWith
                    ? {
                        onRunWith: () =>
                          setRunWith({ title: cap.name, units: cap.units }),
                      }
                    : {})}
                />
              );
            })}
          </div>
        </section>
      ))}

      <section className="panel">
        <h2>
          <button
            type="button"
            className="status-drill-toggle"
            onClick={() => setShowCatchAll((v) => !v)}
          >
            {showCatchAll ? "▾" : "▸"} All actions · {data.catch_all.length}
          </button>
        </h2>
        <p className="subtle">
          Every registered unit not grouped into a capability above — each shown
          with its last run, so nothing escapes the board.
        </p>
        {showCatchAll ? (
          <table className="status-catchall">
            <thead>
              <tr>
                <th>Unit</th>
                <th>Kind</th>
                <th>Last run</th>
                <th>OK</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.catch_all.map((e) => (
                <tr key={`${e.kind}:${e.name}`}>
                  <td>
                    <code>{e.name}</code>
                  </td>
                  <td>{e.kind}</td>
                  <td>{fmtWhen(e.last_at)}</td>
                  <td>
                    {e.ok === null ? "—" : e.ok ? "✓" : `✗${e.status ? ` ${e.status}` : ""}`}
                  </td>
                  <td>
                    {staffActionMap.has(e.name) ? (
                      <button
                        type="button"
                        className="status-runwith-sm"
                        onClick={() =>
                          setRunWith({ title: e.name, units: [e.name] })
                        }
                      >
                        Run…
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>

      {runWith ? (
        <StatusRunModal
          key={runWith.title}
          target={runWith}
          actionMap={staffActionMap}
          onClose={() => {
            setRunWith(null);
            void load();
          }}
        />
      ) : null}
    </div>
  );
}
